import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  Bell, MessageSquareText, Voicemail, PhoneMissed, Inbox,
  Ticket, MailWarning, UserCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useLocation } from "wouter";

interface Notification {
  id: string;
  type: "sms" | "voicemail" | "missed_call" | "assignment" | "inbox_message" | "assigned_conversation_message" | "conversation_unassigned" | "inbox_ticket" | "ticket_reply";
  title: string;
  preview: string;
  count: number;
  newCount?: number;
  timestamp: string;
  entityId: string | null;
  link: string;
  channelBreakdown?: Record<string, number>;
  latestConversationId?: string | null;
}

interface NotificationResponse {
  notifications: Notification[];
  totalCount: number;
  lastSeenAt: string;
}

function getNotificationIcon(type: string) {
  switch (type) {
    case "sms":
      return <MessageSquareText className="h-4 w-4 text-blue-500 shrink-0" />;
    case "voicemail":
      return <Voicemail className="h-4 w-4 text-amber-500 shrink-0" />;
    case "missed_call":
      return <PhoneMissed className="h-4 w-4 text-red-500 shrink-0" />;
    case "assigned_conversation_message":
      return <UserCheck className="h-4 w-4 text-blue-500 shrink-0" />;
    case "inbox_message":
      return <Inbox className="h-4 w-4 text-orange-500 shrink-0" />;
    case "conversation_unassigned":
      return <Inbox className="h-4 w-4 text-orange-500 shrink-0" />;
    case "inbox_ticket":
      return <MailWarning className="h-4 w-4 text-orange-500 shrink-0" />;
    case "ticket_reply":
      return <Ticket className="h-4 w-4 text-purple-500 shrink-0" />;
    default:
      return <Bell className="h-4 w-4 text-muted-foreground shrink-0" />;
  }
}

const CHANNEL_META: Record<string, { label: string; color: string }> = {
  sms:       { label: "SMS",       color: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30" },
  livechat:  { label: "Chat",      color: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" },
  whatsapp:  { label: "WhatsApp",  color: "bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30" },
  facebook:  { label: "Facebook",  color: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border-indigo-500/30" },
  instagram: { label: "Instagram", color: "bg-pink-500/15 text-pink-600 dark:text-pink-400 border-pink-500/30" },
};

const KNOWN_CHANNELS = Object.keys(CHANNEL_META);

function timeAgo(dateStr: string) {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

/** Destination for channel-badge click navigation (independent of filter label). */
type BadgeDest = "assigned" | "inbox" | "unassigned";

function InboxChannelBadges({
  notif,
  onBadgeClick,
}: {
  notif: Notification;
  onBadgeClick: (e: React.MouseEvent, channel: string, dest: BadgeDest) => void;
}) {
  const breakdown = notif.channelBreakdown;
  const entries = breakdown ? Object.entries(breakdown) : [];

  let dest: BadgeDest;
  let filterLabel: string;
  if (notif.type === "assigned_conversation_message") {
    dest = "assigned";
    filterLabel = "unread";
  } else if (notif.type === "conversation_unassigned") {
    dest = "unassigned";
    filterLabel = "unassigned";
  } else {
    dest = "inbox";
    filterLabel = "unread";
  }

  if (entries.length > 0) {
    return (
      <div className="flex flex-wrap gap-1 mt-1.5" onClick={(e) => e.stopPropagation()}>
        {entries.map(([ch, cnt]) => {
          const meta = CHANNEL_META[ch] ?? {
            label: "Inbox",
            color: "bg-orange-500/15 text-orange-600 dark:text-orange-400 border-orange-500/30",
          };
          return (
            <button
              key={ch}
              onClick={(e) => onBadgeClick(e, KNOWN_CHANNELS.includes(ch) ? ch : "", dest)}
              className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border cursor-pointer transition-opacity hover:opacity-75 ${meta.color}`}
              data-testid={`badge-inbox-channel-${ch}`}
            >
              {meta.label} · {cnt} {filterLabel}
            </button>
          );
        })}
      </div>
    );
  }

  // Fallback: single badge showing total count when breakdown is unavailable
  return (
    <div className="flex flex-wrap gap-1 mt-1.5" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={(e) => onBadgeClick(e, "", dest)}
        className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border cursor-pointer transition-opacity hover:opacity-75 bg-orange-500/15 text-orange-600 dark:text-orange-400 border-orange-500/30"
        data-testid="badge-inbox-open"
      >
        {notif.count} {filterLabel} · view inbox
      </button>
    </div>
  );
}

/** Determines whether a notification row shows inline channel badges. */
function hasChannelBadges(type: string): boolean {
  return type === "inbox_message"
    || type === "assigned_conversation_message"
    || type === "conversation_unassigned";
}

/** Shows the text preview row for a notification when a preview string is present. */
function showsPreviewRow(_type: string): boolean {
  // All notification types show their preview text when available.
  // Conversation types (inbox_message, assigned_conversation_message,
  // conversation_unassigned) also show channel badges below the preview row.
  return true;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [, navigate] = useLocation();

  const { data } = useQuery<NotificationResponse>({
    queryKey: ["/api/notifications"],
    // Most Pusher events invalidate this summary immediately. Conversation-inbox
    // updates (new messages) don't go through a global Pusher path, so keep a
    // 5-minute fallback to prevent stale counts when that socket is not active.
    refetchInterval: 300000,
  });

  const markRead = useMutation({
    mutationFn: (type?: string) => apiRequest("POST", "/api/notifications/mark-read", { type }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
  });

  const totalCount = data?.totalCount || 0;
  const notifications = data?.notifications ?? [];

  const handleNotificationClick = (notif: Notification) => {
    setOpen(false);
    if (notif.type === "missed_call") {
      markRead.mutate(notif.type);
    }
    // Navigate to the notification's link. For conversation types the link
    // already encodes the correct tab and selected conversation ID so only
    // the opened conversation is affected — not the whole category.
    navigate(notif.link);
  };

  const handleChannelBadgeClick = (e: React.MouseEvent, channel: string, dest: BadgeDest) => {
    e.stopPropagation();
    setOpen(false);
    if (dest === "assigned") {
      // Navigate to the Active Conversations page with the assigned tab.
      const params = new URLSearchParams({ tab: "assigned" });
      if (channel) params.set("channel", channel);
      navigate(`/conversations/active?${params.toString()}`);
    } else if (dest === "unassigned") {
      const params = new URLSearchParams({ filter: "unassigned" });
      if (channel) params.set("channel", channel);
      navigate(`/conversations/inbox?${params.toString()}`);
    } else {
      // dest === "inbox" → shared Inbox unread
      const params = new URLSearchParams({ filter: "unread" });
      if (channel) params.set("channel", channel);
      navigate(`/conversations/inbox?${params.toString()}`);
    }
  };

  const handleNewVoicemailBadgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    navigate("/voicemails?filter=new");
  };

  const handleMarkAllRead = () => {
    markRead.mutate(undefined);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className="relative"
          data-testid="button-notification-bell"
        >
          <Bell className="h-4 w-4" />
          {totalCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-destructive text-destructive-foreground text-[10px] font-medium flex items-center justify-center px-1"
              data-testid="text-notification-count"
            >
              {totalCount > 99 ? "99+" : totalCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="popover-notifications">
        <div className="flex items-center justify-between gap-2 p-3 border-b">
          <span className="text-sm font-medium">Notifications</span>
          {totalCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={handleMarkAllRead}
              disabled={markRead.isPending}
              data-testid="button-mark-all-read"
            >
              Mark all read
            </Button>
          )}
        </div>

        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground" data-testid="text-no-notifications">
              No new notifications
            </div>
          ) : (
            notifications.map((notif) => (
              <div
                key={notif.id}
                className="w-full p-3 hover-elevate border-b last:border-b-0"
              >
                <button
                  type="button"
                  className="flex w-full items-start gap-3 text-left"
                  onClick={() => handleNotificationClick(notif)}
                  data-testid={`notification-item-${notif.id}`}
                >
                  {getNotificationIcon(notif.type)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium truncate">{notif.title}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-[11px] text-muted-foreground">
                          {timeAgo(notif.timestamp)}
                        </span>
                      </div>
                    </div>

                    {showsPreviewRow(notif.type) && notif.preview ? (
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{notif.preview}</p>
                    ) : null}
                  </div>
                </button>

                {hasChannelBadges(notif.type) ? (
                  <div className="ml-7">
                    <InboxChannelBadges notif={notif} onBadgeClick={handleChannelBadgeClick} />
                  </div>
                ) : null}

                {notif.type === "voicemail" && (notif.newCount ?? 0) > 0 ? (
                  <div className="ml-7 mt-1.5" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={handleNewVoicemailBadgeClick}
                      className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border cursor-pointer transition-opacity hover:opacity-75 bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30"
                      data-testid="badge-notification-new-voicemail"
                    >
                      New VM · {notif.newCount! > 99 ? "99+" : notif.newCount} new
                    </button>
                  </div>
                ) : null}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
